/**
 * Label Editor Component
 * Allows users to change email labels and creates sender rules for learning
 */

import { useState, useEffect } from 'react';
import { IconCheck, IconChevronDown, IconPlus, IconLoader2 } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { createSenderLabelRule, updateSenderLabelRule, deleteSenderLabelRule } from '@/lib/cloudflare/sender-label-rules-client';
import { fetchLabels, type Label as UserLabel } from '@/lib/cloudflare/labels-client';
import { GMAIL_LABEL_COLORS, getTextColorForBackground } from '@/lib/utils/label-colors';

// Module-level cache for labels to avoid repeated API calls
const LABELS_CACHE_KEY = 'winbox_labels_cache';
let labelsCache: UserLabel[] | null = null;
let labelsCachePromise: Promise<UserLabel[]> | null = null;

// Try to load from localStorage immediately on module load
try {
  const cached = localStorage.getItem(LABELS_CACHE_KEY);
  if (cached) {
    labelsCache = JSON.parse(cached);
  }
} catch {
  // Ignore localStorage errors
}

async function getCachedLabels(): Promise<UserLabel[]> {
  if (labelsCache) return labelsCache;

  if (!labelsCachePromise) {
    labelsCachePromise = fetchLabels().then((data) => {
      labelsCache = data.labels || [];
      // Persist to localStorage for instant load on refresh
      try {
        localStorage.setItem(LABELS_CACHE_KEY, JSON.stringify(labelsCache));
      } catch {
        // Ignore localStorage errors
      }
      return labelsCache;
    });
  }

  return labelsCachePromise;
}

// Export for cache invalidation when labels are changed elsewhere
export function invalidateLabelCache() {
  labelsCache = null;
  labelsCachePromise = null;
  try {
    localStorage.removeItem(LABELS_CACHE_KEY);
  } catch {
    // Ignore localStorage errors
  }
}

interface LabelEditorProps {
  currentLabel: string;
  senderEmail: string;
  emailId: string;
  onLabelChange?: (oldLabel: string, newLabel: string) => void;
  className?: string;
}

// Common label suggestions
const COMMON_LABELS = [
  'Invoice',
  'Receipt',
  'Newsletter',
  'Marketing',
  'Work',
  'Personal',
  'Travel',
  'Finance',
  'Shopping',
  'Social',
  'Updates',
  'Notifications',
];

export function LabelEditor({
  currentLabel,
  senderEmail,
  emailId,
  onLabelChange,
  className,
}: LabelEditorProps) {
  const [open, setOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newLabelValue, setNewLabelValue] = useState('');
  const [showNewLabelInput, setShowNewLabelInput] = useState(false);
  // Initialize with cached labels if available (prevents color flash on refresh)
  const [userLabels, setUserLabels] = useState<UserLabel[]>(labelsCache || []);
  const [isLoadingLabels, setIsLoadingLabels] = useState(false);
  const [hasFetchedLabels, setHasFetchedLabels] = useState(!!labelsCache);

  // Fetch labels on mount to get correct colors immediately (uses cache)
  useEffect(() => {
    if (!hasFetchedLabels) {
      getCachedLabels()
        .then((cached) => {
          setUserLabels(cached);
          setHasFetchedLabels(true);
        })
        .catch(console.error);
    }
  }, [hasFetchedLabels]);

  // Show loading state when dropdown opens if labels not yet loaded
  useEffect(() => {
    if (open && !hasFetchedLabels) {
      setIsLoadingLabels(true);
    } else {
      setIsLoadingLabels(false);
    }
  }, [open, hasFetchedLabels]);

  const handleSelectLabel = async (newLabel: string) => {
    if (newLabel === currentLabel) {
      setOpen(false);
      return;
    }

    const oldLabel = currentLabel;
    setIsCreating(true);
    try {
      const result = await createSenderLabelRule({
        senderEmail,
        labelName: newLabel,
        createdFromEmailId: emailId,
      });

      const ruleId = result.rule.id;
      const wasCreated = result.created;

      // Show toast with undo button
      const message = result.created
        ? `${senderEmail} → "${newLabel}"`
        : `Updated: ${senderEmail} → "${newLabel}"`;

      toast(message, {
        duration: 8000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              if (wasCreated) {
                await deleteSenderLabelRule(ruleId);
              } else {
                await updateSenderLabelRule(ruleId, oldLabel);
              }
              // Pass newLabel as old (current state) and oldLabel as new (reverting to)
              onLabelChange?.(newLabel, oldLabel);
              toast.success('Undone');
            } catch (error) {
              console.error('Failed to undo:', error);
              toast.error('Failed to undo');
            }
          },
        },
      });

      onLabelChange?.(oldLabel, newLabel);
      setOpen(false);
    } catch (error) {
      console.error('Failed to create label rule:', error);
      toast.error('Failed to save label preference');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateNewLabel = async () => {
    if (!newLabelValue.trim()) return;
    await handleSelectLabel(newLabelValue.trim());
    setNewLabelValue('');
    setShowNewLabelInput(false);
  };

  // Use shared Gmail color utilities
  const getTextColor = getTextColorForBackground;

  // Default color map for common labels (fallback when not in user labels)
  // Uses Gmail's official 2024-2025 color palette
  const DEFAULT_COLORS: Record<string, string> = {
    'Urgent': '#f28b82',     // Red
    'Important': '#fbbc04',  // Amber
    'Newsletter': '#4285f4', // Strong Blue
    'Marketing': '#d7aefb',  // Purple
    'Invoice': '#34a853',    // Dark Green
    'Receipt': '#81c995',    // Green
    'Shopping': '#fdcfe8',   // Pink
    'Finance': '#fbbc04',    // Amber
    'Travel': '#aecbfa',     // Blue
    'Work': '#4285f4',       // Strong Blue
    'Personal': '#fdcfe8',   // Pink
    'Social': '#d7aefb',     // Purple
    'Updates': '#b7e1cd',    // Light Green
    'Notifications': '#e8eaed', // Gray
  };

  const getLabelColor = (label: string): string => {
    // First check user labels (includes Gmail synced labels)
    const userLabel = userLabels.find(ul => ul.name === label);
    if (userLabel?.color) return userLabel.color;

    // Then check default color map
    if (DEFAULT_COLORS[label]) return DEFAULT_COLORS[label];

    // Hash-based color for unknown labels
    let hash = 0;
    for (let i = 0; i < label.length; i++) {
      hash = ((hash << 5) - hash + label.charCodeAt(i)) | 0;
    }
    return GMAIL_LABEL_COLORS[Math.abs(hash) % GMAIL_LABEL_COLORS.length].bg;
  };

  // Combine common labels with AI labels, removing duplicates
  const allLabels = [...new Set([...COMMON_LABELS, ...userLabels.map(l => l.name)])];

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "text-[10px] font-mono px-1.5 py-0.5 rounded hover:opacity-80 cursor-pointer transition-opacity",
            className
          )}
          style={{
            backgroundColor: getLabelColor(currentLabel),
            color: getTextColor(getLabelColor(currentLabel))
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {currentLabel}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-44 max-h-64 overflow-y-auto"
        align="end"
        onClick={(e) => e.stopPropagation()}
      >
        {isLoadingLabels ? (
          <div className="flex items-center justify-center py-2">
            <IconLoader2 className="h-3 w-3 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {allLabels.map((label) => (
              <DropdownMenuItem
                key={label}
                onClick={() => handleSelectLabel(label)}
                disabled={isCreating}
                className="cursor-pointer gap-1.5"
              >
                <span
                  className="h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: getLabelColor(label) }}
                />
                <span className="flex-1 truncate">{label}</span>
                {currentLabel === label && (
                  <IconCheck className="h-3 w-3 shrink-0 text-muted-foreground" />
                )}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            {showNewLabelInput ? (
              <div className="p-1 flex gap-1" onClick={(e) => e.stopPropagation()}>
                <Input
                  placeholder="Label name"
                  value={newLabelValue}
                  onChange={(e) => setNewLabelValue(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') {
                      handleCreateNewLabel();
                    }
                  }}
                  className="h-6 text-xs"
                  autoFocus
                />
                <Button
                  size="sm"
                  onClick={handleCreateNewLabel}
                  disabled={!newLabelValue.trim() || isCreating}
                  className="h-6 px-2 text-xs"
                >
                  {isCreating ? (
                    <IconLoader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    'Add'
                  )}
                </Button>
              </div>
            ) : (
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setShowNewLabelInput(true);
                }}
                className="cursor-pointer"
              >
                <IconPlus className="h-3 w-3" />
                New label
              </DropdownMenuItem>
            )}
          </>
        )}
        {isCreating && (
          <div className="absolute inset-0 bg-background/50 flex items-center justify-center">
            <IconLoader2 className="h-3.5 w-3.5 animate-spin" />
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
